/**
 * Email Classifier — LLM-powered fashion order classification.
 * Determines if an email is a FASHION order (not food, electronics, etc.)
 * Uses Groq via Strapi backend brain proxy.
 */

import { getBrain } from './webBrain';

/**
 * Classify a single email as fashion or not.
 * @param {object} email - Email with cleanedBody, from, subject
 * @returns {Promise<{isFashion: boolean, confidence: number, reason: string}>}
 */
export async function classifyEmail(email) {
  if (!email.cleanedBody) return { isFashion: false, confidence: 0, reason: 'empty body' };

  const brain = await getBrain();

  const prompt = `Is this email a FASHION order? (clothing, shoes, bags, accessories, jewelry, activewear)
NOT fashion: electronics, food delivery, software, subscriptions, home goods, beauty/skincare-only.

From: ${email.from}
Subject: ${email.subject}
Body: "${email.cleanedBody.slice(0, 400)}"

Reply ONLY with JSON: {"fashion":true/false,"confidence":0.0-1.0,"reason":"brief explanation"}`;

  try {
    const raw = await brain.text(prompt, { temperature: 0.1, maxTokens: 100 });
    console.log(`[Classifier] Raw LLM response for "${email.subject}":`, raw);
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) {
      console.warn(`[Classifier] Could not parse JSON from LLM response for "${email.subject}"`);
      return { isFashion: false, confidence: 0, reason: 'parse error' };
    }

    const parsed = JSON.parse(match[0]);
    const result = {
      isFashion: !!parsed.fashion,
      confidence: typeof parsed.confidence === 'number' ? parsed.confidence : 0.5,
      reason: parsed.reason || ''
    };
    console.log(`[Classifier] "${email.subject}" →`, result);
    return result;
  } catch (err) {
    console.error(`[Classifier] Error classifying "${email.subject}":`, err);
    // On LLM error, pass through (assume it could be fashion)
    return { isFashion: true, confidence: 0.5, reason: 'classification skipped' };
  }
}

/**
 * Classify multiple emails, filtering to fashion-only.
 * @param {Array} emails - Array of email candidates with cleanedBody
 * @param {number} threshold - Confidence threshold (default 0.5)
 * @returns {Promise<Array>} Emails classified as fashion, with classification metadata
 */
export async function classifyFashionEmails(emails, threshold = 0.5) {
  const results = [];

  for (const email of emails) {
    const classification = await classifyEmail(email);
    if (classification.isFashion && classification.confidence >= threshold) {
      results.push({
        ...email,
        classification
      });
    }
  }

  return results;
}
